/**
 * WAYP-394 — Route-level enterprise-feature guard.
 *
 * Modeled after `permission-route.tsx` but checks an enterprise feature
 * flag instead of a permission key. Originally used only by the Manual
 * Fuel module; ATL-000381 follow-up extends it to every module route
 * group (USPS / Dispatch / Intermodal / FTL Brokerage / Livestock) so a
 * login deep-link restore or an org switch onto a module URL lands on
 * /dashboard when that module is disabled for the active org.
 *
 * Behaviour:
 *   - While the enterprise-features query is loading, render a centered
 *     spinner — NOT the page. Rendering children here (the old behavior)
 *     let a disabled-module page mount and fire a burst of 403 API calls
 *     before the redirect kicked in.
 *   - If the query errored, fail OPEN (render the page) so a transient
 *     /features outage can't lock operators out of the whole app. The
 *     server-side `requireFeatureEnabled` gates remain the security
 *     boundary either way.
 *   - Once features have loaded, redirect to /dashboard if the named
 *     feature is OFF for the org.
 *
 * Usage — single route:
 *   <Route path="/fuel/manual-fuel" element={
 *     <RequireFeature featureKey="manual_fuel">
 *       <ManualFuelPage />
 *     </RequireFeature>
 *   } />
 *
 * Usage — layout route wrapping a whole module group (children omitted
 * renders the nested route's <Outlet />):
 *   <Route element={<RequireFeature featureKey="mod_usps_main" />}>
 *     <Route path="/usps/loads" element={<UspsLoadsPage />} />
 *     …
 *   </Route>
 */
import { Navigate, Outlet } from 'react-router-dom';
import { useEnterpriseFeatures } from '../../../hooks/use-enterprise-features';
import { Spinner } from '../ui/spinner';

interface RequireFeatureProps {
  /** Enterprise feature key (e.g. `'manual_fuel'`, `'mod_usps_main'`). */
  featureKey: string;
  /**
   * Page or layout to render when the feature is enabled. Omit when used
   * as a layout route — the nested route renders via <Outlet /> instead.
   */
  children?: React.ReactNode;
}

/** Shared gate body — also consumed by RequireVariantFeature. */
export function FeatureGateBody({ featureKey, children }: RequireFeatureProps) {
  const { data: features, isError } = useEnterpriseFeatures();

  // Query failed — fail open (see header comment).
  if (isError) {
    return children !== undefined ? <>{children}</> : <Outlet />;
  }

  // Still loading — hold the route on a spinner instead of mounting the page.
  if (!features) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  const feature = features.find((f) => f.key === featureKey);
  if (!feature?.isEnabled) {
    return <Navigate to="/dashboard" replace />;
  }

  return children !== undefined ? <>{children}</> : <Outlet />;
}

export function RequireFeature(props: RequireFeatureProps) {
  return <FeatureGateBody {...props} />;
}

export default RequireFeature;
